// The Lineage tab's alternate views. Geometry lives in `lib/lineageLayouts.js`;
// these components map it to SVG and own interaction.
//
// They deliberately copy the TERMS ARCH panel's chrome rather than inventing
// their own, because the first pass did invent its own and read as a foreign
// component dropped into the tab: a fixed-height SVG floating in a tall panel, a
// row of coloured legend dashes, and an explanatory paragraph. The house pattern
// for a graph panel in this lens is:
//
//   flex-col h-full
//     top bar     — the ENCODING, one NOTE_CLASS line, border-b
//     measured host — flex-1, ResizeObserver, SVG fills it
//     bottom bar  — hover DETAIL, or the affordance hint, border-t
//
// So: no legend swatches (the encoding line says it), no prose paragraph (the
// bottom bar says it), and never a fixed height (the dome has to fill the panel
// the way the Terms arch does).
//
// House rules that bite here: no gradients (colour-mix alpha instead), no
// italics, and never a native `title=` — HoverTooltip or nothing.
import { useEffect, useMemo, useRef, useState } from "react";
import { arcLayout, archInput, laneLayout, matrixLayout, depthLayout } from "../../lib/lineageLayouts.js";
import { archFrame, buildArch, archRibbonPath } from "../../lib/termArch.js";
import { relOf } from "../../lib/lineageView.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import HoverTooltip from "../HoverTooltip.jsx";
import ScrollArea from "../ScrollArea.jsx";

// Tier drives colour. `cited` is the only warm hue on purpose — it is the
// strongest claim, and noticing whether it ever appears is the point of several
// of these views.
export const TIER_COLOR = {
  cited: "var(--amber)",
  quoted: "var(--info)",
  entity: "var(--green)",
  adjacent: "var(--muted-soft)",
};
export const TIER_LABEL = { cited: "cited", quoted: "quoted", entity: "related", adjacent: "consulted" };
const FAMILY_TINT = {
  file: "var(--green)",
  query: "var(--info)",
  forge: "var(--amber)",
  web: "var(--green)",
  linear: "var(--info)",
  artifact: "var(--green)",
  recall: "var(--info)",
};
// Default is GREEN, not grey. A family with no entry rendered as a dead grey
// sliver next to green arcs, which is what made the arch look like two charts.
const famTint = (f) => FAMILY_TINT[f] || "var(--green)";
const strokeFor = (tier) => (tier === "adjacent" ? 1 : 1.6);
const opacityFor = (tier) => (tier === "adjacent" ? 0.4 : 0.75);

const clipLabel = (t, max) => (t.length > max ? `${t.slice(0, max - 1)}…` : t);

const shortLabel = (node, prefix) => {
  if (node.kind?.startsWith("file") && node.ref) {
    // Separators are normalised first: a Windows ref carries no forward slash, so
    // splitting on "/" alone returned the whole "C:\\Users\\…\\Template.json".
    const rel = relOf(node.ref, prefix).replace(/\\/g, "/");
    return rel.slice(rel.lastIndexOf("/") + 1) || rel;
  }
  if (node.kind === "web.fetch" && node.ref) return node.ref.replace(/^https?:\/\//, "").split("/")[0];
  return node.ref || node.label || node.id;
};

/**
 * The panel chrome every view shares, matching the Terms arch exactly.
 *
 * `encoding` is what the axes MEAN — it belongs above the graph, once, as prose
 * in the note style, not as colour swatches. `hint` is the affordance, and
 * `detail` replaces it while something is hovered, which is how Terms turns its
 * footer into a readout instead of adding a tooltip.
 */
function ViewFrame({ encoding, hint, detail, minHeight = 280, children }) {
  const hostRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((es) => {
      const cr = es[0]?.contentRect;
      if (cr) setSize({ w: Math.floor(cr.width), h: Math.floor(cr.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const W = Math.max(560, size.w || 900);
  const H = Math.max(minHeight, size.h || minHeight);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-w-0 border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={NOTE_CLASS}>{encoding}</span>
      </div>
      <div ref={hostRef} className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {children({ W, H, measured: size.w > 0 })}
      </div>
      <div className="border-t border-[color:var(--line-soft)] px-[12px] py-[5px]">
        <span className={NOTE_CLASS}>{detail || hint}</span>
      </div>
    </div>
  );
}

// ── ARCH ─────────────────────────────────────────────────────────────────────

/**
 * The Terms arch, over artifacts instead of terms.
 *
 * Deliberately a near-copy of the Terms renderer — same archFrame, same P and
 * segPath, same radial labels with a count tspan, same weight-scaled single-hue
 * ribbons with the incident/dimmed focus states, same footer readout. The point
 * is that it should be indistinguishable as a component; only the nouns change.
 *
 * Ribbons are ONE hue, as Terms is. Tier lives in the footer readout rather than
 * in four ribbon colours — colouring them was what made this read as a different
 * visual family, and weight is the thing the eye should pick up first.
 */
export function LineageArch({ built, prefix, onSelectTrace }) {
  const [hover, setHover] = useState(null);
  const input = useMemo(() => archInput(built, { limit: 40 }), [built]);
  const arch = useMemo(() => buildArch(input.items, input.edges), [input]);
  const byTerm = useMemo(() => new Map(input.items.map((i) => [i.term, i])), [input]);
  const focusRibbons = useMemo(
    () => arch.ribbons.filter((r) => r.a === hover || r.b === hover),
    [arch, hover],
  );
  const neighbors = useMemo(
    () => new Set(focusRibbons.map((r) => (r.a === hover ? r.b : r.a))),
    [focusRibbons, hover],
  );
  if (!input.items.length) {
    return <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>No nodes in this window.</span>;
  }
  const hovered = hover ? byTerm.get(hover) : null;
  const partners = focusRibbons
    .slice()
    .sort((x, y) => y.weight - x.weight)
    .map((r) => {
      const other = byTerm.get(r.a === hover ? r.b : r.a);
      return `${shortLabel(other.node, prefix)} (${TIER_LABEL[r.tier]})`;
    });

  return (
    <ViewFrame
      minHeight={340}
      encoding={
        `${input.shown < input.total ? `top ${input.shown} of ${input.total} artifacts` : `${input.shown} artifacts`}`
        + " · arch length = ops touching it · ribbons = provenance edges between them"
        // Said out loud: an op whose lens resolves no identity cannot be a
        // segment, and silently dropping half the graph is worse than a number.
        + (input.unnamed ? ` · ${input.unnamed} op${input.unnamed === 1 ? "" : "s"} named no artifact` : "")
      }
      hint="hover an artifact to trace what fed it · click to open the trace"
      detail={hovered ? (
        <>
          <span className="text-[color:var(--ink)]">{shortLabel(hovered.node, prefix)}</span>
          {` · ${hovered.count} op${hovered.count === 1 ? "" : "s"}`}
          {partners.length ? ` · linked to ${partners.slice(0, 4).join(", ")}${partners.length > 4 ? ` +${partners.length - 4}` : ""}` : " · no provenance edge either way"}
        </>
      ) : null}
    >
      {({ W, H }) => {
        const { cx, cy, rx, ry, psi } = archFrame(W, H);
        const sweep = Math.PI + 2 * psi;
        const P = (t, dr = 0) => {
          const th = -psi + (t / Math.PI) * sweep;
          const px = cx + rx * Math.cos(th);
          const py = cy - ry * Math.sin(th);
          if (!dr) return [px, py];
          const vx = px - cx; const vy = py - cy;
          const n = Math.sqrt(vx * vx + vy * vy) || 1;
          return [px + (vx / n) * dr, py + (vy / n) * dr];
        };
        const segPath = (a, b, dr = 0) => {
          const steps = Math.max(2, Math.ceil((a - b) * (sweep / Math.PI) * 24));
          let d = "";
          for (let i = 0; i <= steps; i++) {
            const [x, y] = P(a + (b - a) * (i / steps), dr);
            d += `${i ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)} `;
          }
          return d;
        };
        const maxCount = Math.max(...input.items.map((i) => i.count));
        return (
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
            aria-label="Lineage arch. Hover over an artifact to trace its inputs."
            onMouseLeave={() => setHover(null)}>
            {arch.ribbons
              .slice()
              .sort((x, y) => x.weight - y.weight)
              .map((r) => {
                if (!r.spanA || !r.spanB) return null;
                const ma = P((r.spanA[0] + r.spanA[1]) / 2);
                const mb = P((r.spanB[0] + r.spanB[1]) / 2);
                const k = 0.58;
                const ctrl = [((ma[0] + mb[0]) / 2) * (1 - k) + cx * k, ((ma[1] + mb[1]) / 2) * (1 - k) + cy * k];
                const t = r.weight / arch.maxWeight;
                const incident = hover && (r.a === hover || r.b === hover);
                const opacity = incident ? 0.5 + 0.25 * t : hover ? 0.04 : 0.1 + 0.26 * t;
                return (
                  <path key={`${r.a}|${r.b}`} fill="var(--green)" opacity={opacity}
                    d={archRibbonPath(P, r.spanA, r.spanB, ctrl)} />
                );
              })}
            {arch.order.map((term) => {
              const it = byTerm.get(term);
              const seg = arch.segments.get(term);
              if (!it || !seg) return null;
              const { a, b } = seg;
              const isFocus = term === hover;
              const isNeighbor = neighbors.has(term);
              const [lx, ly] = P((a + b) / 2, 14);
              const phi = (Math.atan2(ly - cy, lx - cx) * 180) / Math.PI;
              const flip = phi > 90 || phi < -90;
              const big = it.count >= maxCount * 0.4;
              return (
                <g key={term} style={{ cursor: "pointer" }}
                  opacity={hover && !isFocus && !isNeighbor ? 0.4 : 1}
                  onMouseEnter={() => setHover(term)}
                  onClick={() => it.node.promptId && onSelectTrace?.(it.node.promptId)}>
                  <path d={segPath(a, b)} fill="none" stroke={famTint(it.family)} strokeWidth={isFocus ? 12 : 9} />
                  <text x={lx.toFixed(1)} y={(ly + 3.5).toFixed(1)} textAnchor={flip ? "end" : "start"}
                    fontSize={big ? 11.5 : 10} fontWeight={isFocus || big ? 500 : 400}
                    fill={isFocus ? "var(--green)" : hover ? (isNeighbor ? "var(--ink)" : "var(--muted-soft)") : (big ? "var(--ink)" : "var(--muted)")}
                    transform={`rotate(${(flip ? phi + 180 : phi).toFixed(1)} ${lx.toFixed(1)} ${ly.toFixed(1)})`}>
                    {/* archFrame reserves padX for labels; a long basename still
                        overruns it and clips at the panel edge, so cap here. */}
                    {clipLabel(shortLabel(it.node, prefix), 24)}
                    <tspan fill="var(--muted-soft)" fontSize="8.5">{` ${it.count}`}</tspan>
                  </text>
                  {/* Forgiving hit band over the segment, as Terms does. */}
                  <path d={segPath(a, b)} fill="none" stroke="transparent" strokeWidth={24} />
                </g>
              );
            })}
          </svg>
        );
      }}
    </ViewFrame>
  );
}

// ── ARC ──────────────────────────────────────────────────────────────────────

export function LineageArc({ built, prefix, onSelectTrace }) {
  const [hover, setHover] = useState(null);
  return (
    <ViewFrame
      minHeight={300}
      encoding="one time axis · above = derived, below = consulted · arc height = distance apart · dim ticks reached no edge"
      hint="hover a node to isolate its links · click to open the trace"
      detail={hover ? <span className="text-[color:var(--ink)]">{hover}</span> : null}
    >
      {({ W, H }) => {
        const L = arcLayout(built, { width: W, height: H - 26 });
        if (!L.nodes.length) return null;
        const showLabels = L.nodes.length <= 46;
        return (
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
            aria-label={`Lineage arc: ${L.nodes.length} nodes, ${L.arcs.length} edges, ${L.unlinked} unlinked`}
            onMouseLeave={() => setHover(null)}>
            <line x1={16} y1={L.axisY} x2={W - 16} y2={L.axisY} stroke="var(--line)" strokeWidth={1} />
            {L.arcs.map((a) => {
              const on = !hover || a.edge.source.id === hover || a.edge.target.id === hover;
              return (
                <path key={a.id} d={a.d} fill="none" stroke={TIER_COLOR[a.tier]}
                  strokeWidth={strokeFor(a.tier)} opacity={on ? opacityFor(a.tier) : 0.05} />
              );
            })}
            {L.nodes.map((n) => (
              <g key={n.id} style={{ cursor: n.promptId ? "pointer" : "default" }}
                onMouseEnter={() => setHover(n.id)}
                onClick={() => n.promptId && onSelectTrace?.(n.promptId)}>
                <circle cx={n.x} cy={n.y} r={n.linked ? 3.6 : 1.8}
                  fill={n.linked ? famTint(n.family) : "var(--muted-soft)"} opacity={n.linked ? 1 : 0.4} />
                <circle cx={n.x} cy={n.y} r={9} fill="transparent" />
                {showLabels ? (
                  <text transform={`translate(${n.x},${n.y + 13}) rotate(38)`}
                    fontSize={n.linked ? 9.5 : 8.5}
                    fill={n.linked ? "var(--muted)" : "var(--muted-soft)"}>
                    {shortLabel(n, prefix)}
                  </text>
                ) : null}
              </g>
            ))}
          </svg>
        );
      }}
    </ViewFrame>
  );
}

// ── LANES ────────────────────────────────────────────────────────────────────

export function LineageLanes({ built, onSelectTrace }) {
  const [hover, setHover] = useState(null);
  return (
    <ViewFrame
      minHeight={240}
      encoding="time across · one lane per session · edges only ever form inside a lane, because provenance is within-session"
      hint="Very short lanes cannot show a chain. Widen the window to include more activity."
      detail={hover}
    >
      {({ W }) => {
        const L = laneLayout(built, { width: W });
        if (!L.lanes.length) return null;
        const secs = (ms) => (ms >= 1000 ? `${Math.round(ms / 1000)}s` : "same instant");
        return (
          <svg viewBox={`0 0 ${W} ${L.height}`} width="100%" height={L.height} role="img"
            aria-label={`Lineage by session: ${L.lanes.length} lanes`} onMouseLeave={() => setHover(null)}>
            {L.lanes.map((l, i) => (
              <g key={l.sessionId || i}>
                <line x1={120} y1={l.y} x2={W - 30} y2={l.y} stroke="var(--line-soft)" />
                <text x={108} y={l.y + 3} textAnchor="end" fontSize={9.5} fill="var(--muted-soft)">
                  {`session ${i + 1} · ${l.count} · ${secs(l.spanMs)}`}
                </text>
                {l.nodes.map((n) => (
                  <g key={n.id} style={{ cursor: n.promptId ? "pointer" : "default" }}
                    onMouseEnter={() => setHover(`${l.count} nodes over ${secs(l.spanMs)}`)}
                    onClick={() => n.promptId && onSelectTrace?.(n.promptId)}>
                    <circle cx={n.x} cy={n.y} r={n.linked ? 3.4 : 1.8}
                      fill={n.linked ? famTint(n.family) : "var(--muted-soft)"} opacity={n.linked ? 1 : 0.4} />
                    <circle cx={n.x} cy={n.y} r={8} fill="transparent" />
                  </g>
                ))}
              </g>
            ))}
            {L.arcs.map((a) => (
              <path key={a.id} d={a.d} fill="none" stroke={TIER_COLOR[a.tier]}
                strokeWidth={strokeFor(a.tier)} opacity={opacityFor(a.tier)} />
            ))}
          </svg>
        );
      }}
    </ViewFrame>
  );
}

// ── GRID ─────────────────────────────────────────────────────────────────────

export function LineageMatrix({ built }) {
  const [hover, setHover] = useState(null);
  const L = matrixLayout(built, { cell: 26 });
  if (!L.fams.length) {
    return <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>No nodes in this window.</span>;
  }
  const ROW_LABEL_W = 46;
  const GAP = 22;
  return (
    <ViewFrame
      minHeight={200}
      encoding="rows = what was produced · columns = what fed it · one grid per tier · darker = more edges"
      hint="An empty grid means this tier had no activity."
      detail={hover}
    >
      {({ W, H }) => {
        // Cells are sized from the MEASURED box rather than fixed at 26px, so the
        // four grids fill the panel instead of huddling top-left with the last
        // one clipped off the right edge. Bounded below so a short panel scrolls
        // (through ScrollArea) rather than collapsing to unreadable specks.
        const n = L.fams.length;
        const byW = Math.floor((W - ROW_LABEL_W - GAP * L.grids.length - 24) / (L.grids.length * n));
        const byH = Math.floor((H - 74) / n);
        const cell = Math.max(14, Math.min(46, byW, byH));
        return (
          <ScrollArea className="h-full min-h-0" contentClassName="min-w-0">
            <div className="flex min-w-max px-[12px] py-[8px]" style={{ gap: GAP }}
              onMouseLeave={() => setHover(null)}>
              {L.grids.map((g) => (
                <div key={g.tier} className="shrink-0">
                  <div className="mb-[5px] flex items-baseline gap-[6px]"
                    style={{ marginLeft: g.tier === "cited" ? ROW_LABEL_W : 0 }}>
                    <span className="text-[10px]" style={{ color: TIER_COLOR[g.tier] }}>{TIER_LABEL[g.tier]}</span>
                    <span className="text-[10px] tabular-nums text-[color:var(--muted-soft)]">{g.total}</span>
                  </div>
                  <div className="flex">
                    {/* Row labels once, on the first grid — repeating them four
                        times costs more width than the grids themselves. */}
                    {g.tier === "cited" ? (
                      <div className="grid" style={{ width: ROW_LABEL_W, gridTemplateRows: `repeat(${n}, ${cell}px)` }}>
                        {L.fams.map((f) => (
                          <div key={f} className="flex items-center justify-end overflow-hidden pr-[4px] text-[9px] text-[color:var(--muted-soft)]">{f}</div>
                        ))}
                      </div>
                    ) : null}
                    <div className="grid" style={{
                      gridTemplateColumns: `repeat(${n}, ${cell}px)`,
                      gridTemplateRows: `repeat(${n}, ${cell}px)`,
                    }}>
                      {g.cells.map((c) => (
                        <div key={`${c.sf}-${c.tf}`}
                          onMouseEnter={() => setHover(c.n
                            ? `${c.n} ${TIER_LABEL[g.tier]} · ${c.sf} fed ${c.tf}`
                            : `no ${TIER_LABEL[g.tier]} edge from ${c.sf} to ${c.tf}`)}
                          className="flex items-center justify-center tabular-nums"
                          style={{
                            margin: 1.5,
                            fontSize: Math.min(10, cell * 0.38),
                            background: c.n
                              ? `color-mix(in srgb, ${TIER_COLOR[g.tier]} ${Math.round(26 + (c.n / Math.max(1, g.max)) * 60)}%, transparent)`
                              : "var(--line-soft)",
                            color: c.n ? "var(--bg)" : "transparent",
                          }}>
                          {c.n || ""}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-[3px] flex" style={{ marginLeft: g.tier === "cited" ? ROW_LABEL_W : 0 }}>
                    {L.fams.map((f) => (
                      <div key={f} className="overflow-hidden text-[8.5px] text-[color:var(--muted-soft)]"
                        style={{ width: cell }}>{f.slice(0, 4)}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        );
      }}
    </ViewFrame>
  );
}

// ── DEPTH ────────────────────────────────────────────────────────────────────

export function LineageDepth({ built, prefix, onSelectTrace }) {
  const [hover, setHover] = useState(null);
  const L = depthLayout(built, { colW: 240, rowH: 21 });
  if (!L.columns.length) {
    return <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>Nothing was produced in this window.</span>;
  }
  const deepest = Math.max(...L.columns.map((c) => c.depth));
  return (
    <ViewFrame
      minHeight={220}
      encoding={`columns = steps back from something produced · deepest chain here is ${deepest} step${deepest === 1 ? "" : "s"}`}
      hint="everything in one column means the window holds no chains · click to open the trace"
      detail={hover ? <span className="text-[color:var(--ink)]">{hover}</span> : null}
    >
      {() => (
        <ScrollArea className="h-full min-h-0" contentClassName="min-w-0">
          <svg viewBox={`0 0 ${L.width} ${L.height}`} width={L.width} height={L.height} role="img"
            aria-label={`Lineage by depth: ${L.columns.length} columns`} onMouseLeave={() => setHover(null)}>
            {L.links.map((l) => (
              <path key={l.id} d={l.d} fill="none" stroke={TIER_COLOR[l.tier]}
                strokeWidth={strokeFor(l.tier)} opacity={opacityFor(l.tier) * 0.85} />
            ))}
            {L.columns.map((c) => (
              <g key={c.depth}>
                <text x={c.x} y={20} fontSize={9.5} fill="var(--muted-soft)">{`${c.label} · ${c.count}`}</text>
                <line x1={c.x - 8} y1={28} x2={c.x + 200} y2={28} stroke="var(--line-soft)" />
                {c.nodes.map((n) => (
                  <g key={n.id} style={{ cursor: n.promptId ? "pointer" : "default" }}
                    onMouseEnter={() => setHover(shortLabel(n, prefix))}
                    onClick={() => n.promptId && onSelectTrace?.(n.promptId)}>
                    <circle cx={n.x + 4} cy={n.y} r={c.depth === 0 ? 3.6 : 2.4} fill={famTint(n.family)} />
                    <text x={n.x + 14} y={n.y + 3} fontSize={9.5} fill="var(--muted)">
                      {shortLabel(n, prefix).slice(0, 30)}
                    </text>
                  </g>
                ))}
              </g>
            ))}
            {L.unlinkedNodes.length ? (
              <g>
                <text x={L.width - 170} y={20} fontSize={9.5} fill="var(--muted-soft)">
                  {`unlinked · ${L.unlinkedNodes.length}`}
                </text>
                {L.unlinkedNodes.slice(0, 16).map((n, i) => (
                  <text key={n.id} x={L.width - 170} y={44 + i * 15} fontSize={8.5} fill="var(--muted-soft)">
                    {shortLabel(n, prefix).slice(0, 24)}
                  </text>
                ))}
              </g>
            ) : null}
          </svg>
        </ScrollArea>
      )}
    </ViewFrame>
  );
}
